import { Col, Grid, RowWrap } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import ProfilePicture from "@govlaunch/profile-picture";
import { chunk } from "lodash/fp";
import Link from "next/link";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import DelayedHover from "~/components/DelayedHover";
import useIsMobile from "~/lib/hooks/useIsMobile";
import useIsTablet from "~/lib/hooks/useIsTablet";
import useShowMoreCounter from "~/lib/hooks/useShowMoreCounter";
import { IGovernmentUser, IUser, IVendorUser } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import AnonymousUser from "~/components/icons/AnonymousUser";
import Collaborator from "~/components/invite/Collaborator";
import InviteModal from "~/components/invite/InviteModal";
import { Margin } from "~/components/spacings";
import { ShowMore } from "~/components/profile/ShowMore";

type TARGET_TYPE = "GOVERNMENT" | "VENDOR";

type TMember =
  | Pick<
      IGovernmentUser,
      "__typename" | "_id" | "slug" | "fullName" | "avatar" | "isInnovator" | "jobTitle" | "firstName"
    >
  | Pick<IVendorUser, "__typename" | "_id" | "slug" | "fullName" | "avatar" | "jobTitle" | "firstName">;

interface ISingleTeamMember {
  member: TMember;
  viewerIsMember: boolean;
  targetType: TARGET_TYPE;
  targetId: string;
}

interface ITeamProps {
  members: TMember[] | null;
  viewerIsMember: boolean;
  viewerCanInviteMembers: boolean;
  viewerCanRemoveMembers: boolean;
  viewerCanSeeTeam?: boolean;
  targetType: TARGET_TYPE;
  targetId: string;
}

const Team: React.FunctionComponent<ITeamProps> = ({
  members,
  viewerIsMember,
  viewerCanInviteMembers,
  viewerCanRemoveMembers,
  viewerCanSeeTeam = true,
  targetType,
  targetId,
}) => {
  const intl = useIntl();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const { offset, hasMore, showMore } = useShowMoreCounter((members || []).length, isTablet ? 3 : 8);

  if (!viewerCanSeeTeam || (members?.length === 0 && !viewerCanInviteMembers)) {
    return (
      <div
        css={{
          backgroundColor: palette.white,
          borderRadius: 5,
          border: `solid 1px ${palette.lightestGray}`,
          position: "relative",
        }}
      >
        <div
          css={{
            padding: 16,
            borderBottom: `solid 1px ${palette.lightestGray}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            css={{
              textTransform: "uppercase",
              fontSize: 12,
              color: palette.sealBlue,
              letterSpacing: 0.5,
              fontWeight: 500,
            }}
          >
            <FormattedMessage defaultMessage="Team" id="wsUmh9" />
          </span>
        </div>
        <div
          css={{
            padding: 16,
            fontSize: 15,
            textAlign: "center",
            color: palette.darkGray,
          }}
        >
          🚫 <FormattedMessage defaultMessage="You do not have access to view this content" id="x2z+82" />
        </div>
      </div>
    );
  }

  if (members!.length === 1) {
    return (
      <SingleTeamMember
        member={members![0]}
        targetType={targetType}
        targetId={targetId}
        viewerIsMember={viewerIsMember}
      />
    );
  }

  return (
    <div
      data-intercom-target={viewerIsMember ? `${targetType === "GOVERNMENT" ? "Government" : "Vendor"} Team` : null}
      css={{
        backgroundColor: palette.white,
        borderRadius: 5,
        border: `solid 1px ${palette.lightestGray}`,
        position: "relative",
      }}
    >
      <div
        css={{
          padding: 15,
          borderBottom: `solid 1px ${palette.lightestGray}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          css={{
            textTransform: "uppercase",
            fontSize: 12,
            color: palette.sealBlue,
            letterSpacing: 0.5,
            fontWeight: 500,
          }}
        >
          <FormattedMessage defaultMessage="Team ({membersCount})" id="9HIlM0" values={{ membersCount: members!.length }} />
        </span>

        {targetType === "GOVERNMENT" && viewerCanInviteMembers && (
          <Toggle>
            {({ on: isOpen, toggle }) => (
              <>
                <span
                  role="button"
                  aria-label="Invite your team"
                  className="invite-government-team"
                  onClick={toggle}
                  css={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: palette.primary,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  <FormattedMessage defaultMessage="Invite others" id="+s1O1O" />
                </span>

                <InviteModal
                  title={intl.formatMessage({ defaultMessage: 'Invite Team Members', id: 'LeF5J5' })}
                  subtitle={
                    <span>
                      <FormattedMessage
                        defaultMessage="They’ll be granted access to your <strong>Government</strong>" id="HH5Cyh"
                        values={{
                          // eslint-disable-next-line react/display-name
                          strong: (text: string) => <strong>{text}</strong>,
                        }}
                      />
                    </span>
                  }
                  targetType={targetType}
                  targetId={targetId}
                  isOpen={isOpen}
                  onRequestClose={toggle}
                />
              </>
            )}
          </Toggle>
        )}
      </div>

      {isMobile ? (
        <div
          css={{
            padding: 10,
          }}
        >
          <Grid gutter={15}>
            <RowWrap>
              {members!.slice(0, offset).map((member) => {
                return (
                  <Col key={member!._id} mt={10} mb={10} width={1}>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ProfilePicture
                        size={48}
                        image={member.avatar}
                        name={member.fullName}
                        innovator={member.__typename === "GovernmentUser" ? member.isInnovator : false}
                      />

                      <div
                        css={{
                          marginLeft: 15,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <span
                          css={{
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          {member!.fullName}
                        </span>

                        <span
                          css={{
                            fontSize: 14,
                            color: palette.mediumGray,
                          }}
                        >
                          {member!.jobTitle}
                        </span>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </RowWrap>
          </Grid>
        </div>
      ) : (
        <Grid gutter={15}>
          {chunk(4, members!.slice(0, offset)).map((row, index) => {
            return (
              <RowWrap key={index} mt={15} mb={15} justifyContent="flex-start">
                {row.map((member) => {
                  return (
                    <Col
                      key={member._id}
                      width={3 / 12}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {member.fullName ? (
                        <Collaborator
                          targetType={targetType}
                          targetId={targetId}
                          viewerCanRemove={viewerCanRemoveMembers || false}
                          user={member as IUser}
                          removeText="Remove from Team"
                          size={60}
                        />
                      ) : (
                        <DelayedHover ms={300}>
                          {({ hover, ...bind }) => (
                            <Popover
                              placement="top"
                              isOpen={hover}
                              backgroundColor={palette.innovatorBlue}
                              borderColor={palette.innovatorBlue}
                              style={{
                                boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                              }}
                              render={() => (
                                <div
                                  css={{
                                    padding: 10,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    color: palette.white,
                                  }}
                                >
                                  <span
                                    css={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {member.fullName}
                                  </span>

                                  <span
                                    css={{
                                      fontSize: 12,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {member.jobTitle}
                                  </span>
                                </div>
                              )}
                            >
                              {({ innerRef }: { innerRef: React.RefObject<HTMLDivElement> }) => (
                                <div ref={innerRef} {...bind}>
                                  <AnonymousUser width={60} height={60} />
                                </div>
                              )}
                            </Popover>
                          )}
                        </DelayedHover>
                      )}
                    </Col>
                  );
                })}
              </RowWrap>
            );
          })}
        </Grid>
      )}

      {hasMore && <ShowMore onClick={showMore} />}
    </div>
  );
};

const SingleTeamMember: React.FunctionComponent<ISingleTeamMember> = ({
  member,
  viewerIsMember,
  targetType,
  targetId,
}) => {
  const user = useSelfie();
  const intl = useIntl();

  return (
    <div
      data-intercom-target={viewerIsMember ? "Government Team" : null}
      css={{
        backgroundColor: palette.white,
        borderRadius: 5,
        border: `solid 1px ${palette.lightestGray}`,
        position: "relative",
      }}
    >
      <div
        css={{
          padding: 15,
          textTransform: "uppercase",
          fontSize: 12,
          color: palette.sealBlue,
          display: "flex",
          alignItems: "center",
          letterSpacing: 0.5,
          fontWeight: 500,
          borderBottom: `solid 1px ${palette.lightestGray}`,
        }}
      >
        {user && user._id === member._id ? (
          <span>
            <FormattedMessage defaultMessage="For now it's just you!" id="lfNa1Y" />
          </span>
        ) : (
          <span>
            <FormattedMessage defaultMessage="Team" id="wsUmh9" />
          </span>
        )}

        {targetType === "GOVERNMENT" && viewerIsMember && (
          <Toggle>
            {({ on: isOpen, toggle }) => (
              <>
                <span
                  role="button"
                  aria-label="Invite your team"
                  css={{
                    marginLeft: "auto",
                    fontWeight: 600,
                    fontSize: 11,
                    color: palette.primary,
                    cursor: "pointer",
                  }}
                  onClick={toggle}
                >
                  <FormattedMessage defaultMessage="Invite others" id="+s1O1O" />
                </span>

                <InviteModal
                  title={intl.formatMessage({ defaultMessage: 'Invite Team Members', id: 'LeF5J5' })}
                  subtitle={
                    <span>
                      <FormattedMessage
                        defaultMessage="They’ll be granted access to your <strong>Government</strong>" id="HH5Cyh"
                        values={{
                          // eslint-disable-next-line react/display-name
                          strong: (text: string) => <strong>{text}</strong>,
                        }}
                      />
                    </span>
                  }
                  targetType={targetType}
                  targetId={targetId}
                  isOpen={isOpen}
                  onRequestClose={toggle}
                />
              </>
            )}
          </Toggle>
        )}
      </div>

      <div
        css={{
          padding: 15,
          display: "flex",
          justifyContent: "center",
          alignItem: "center",
          flexDirection: "column",
        }}
      >
        {member.slug ? (
          <Link passHref={true} href="/[userSlug]/[[...tab]]" as={`/@${member.slug}`}>
            <a
              css={{
                margin: 0,
                display: "flex",
                textDecoration: "none",
                justifyContent: "center",
              }}
            >
              {member.fullName ? (
                <ProfilePicture
                  size={80}
                  name={member.fullName}
                  image={member.avatar}
                  innovator={member.__typename === "GovernmentUser" ? member.isInnovator : false}
                />
              ) : (
                <div
                  css={{
                    width: 80,
                    height: 80,
                  }}
                >
                  <AnonymousUser width={80} height={80} />
                </div>
              )}
            </a>
          </Link>
        ) : (
          <div
            css={{
              width: 80,
              height: 80,
              margin: "0 auto",
            }}
          >
            <AnonymousUser width={80} height={80} />
          </div>
        )}

        <Margin mt={10}>
          <div
            css={{
              textAlign: "center",
            }}
          >
            {member.fullName && (
              <Link passHref={true} href="/[userSlug]/[[...tab]]" as={`/@${member.slug}`}>
                <a
                  css={{
                    color: palette.text,
                    textDecoration: "none",
                  }}
                >
                  <h4
                    css={{
                      margin: 0,
                    }}
                  >
                    {member.fullName || member.firstName}
                  </h4>
                </a>
              </Link>
            )}

            {!member.fullName && member.firstName && (
              <h4
                css={{
                  margin: 0,
                }}
              >
                {member.firstName}
              </h4>
            )}

            <span
              css={{
                fontSize: 12,
                color: palette.sealBlue,
              }}
            >
              {member.jobTitle}
            </span>
          </div>
        </Margin>
        {member.__typename === "GovernmentUser" && member.isInnovator && (
          <Margin mt={10} mb={10}>
            <span
              css={{
                padding: 5,
                paddingLeft: 10,
                paddingRight: 10,
                border: `solid 1px ${palette.innovatorBlue}`,
                color: palette.innovatorBlue,
                fontSize: 12,
                borderRadius: 20,
                fontWeight: "bold",
                cursor: "default",
                userSelect: "none",
              }}
            >
              <FormattedMessage defaultMessage="Innovator" id="0RwIiV" />
            </span>
          </Margin>
        )}
      </div>
    </div>
  );
};

export default Team;
